import { type DropResult } from "@hello-pangea/dnd";
import { Plus } from "lucide-react";
import {
  useDataProvider,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  KanbanBoard,
  KanbanColumn,
  KanbanDropZone,
} from "../../atomic-crm/misc/Kanban";
import { ListSkeleton } from "../../atomic-crm/misc/ListSkeleton";
import { TEAM_TASK_STATUSES } from "../common/constants";
import type { TeamTask, TeamTaskStatus } from "../types";
import { TeamTaskCard } from "./TeamTaskCard";

type Columns = Record<TeamTaskStatus, TeamTask[]>;

const toColumns = (tasks: TeamTask[]): Columns => {
  const columns = Object.fromEntries(
    TEAM_TASK_STATUSES.map((status) => [status.id, [] as TeamTask[]]),
  ) as Columns;
  for (const task of tasks) columns[task.status]?.push(task);
  for (const status of Object.keys(columns) as TeamTaskStatus[])
    columns[status].sort(
      (a, b) => a.index - b.index || Number(a.id) - Number(b.id),
    );
  return columns;
};

export const TeamTaskKanban = () => {
  const { data, isPending } = useListContext<TeamTask>();
  const [columns, setColumns] = useState<Columns>(() => toColumns([]));
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  useEffect(() => {
    if (data) setColumns(toColumns(data));
  }, [data]);

  const onDragEnd = async ({ source, destination }: DropResult) => {
    if (!destination) return;
    const from = source.droppableId as TeamTaskStatus;
    const to = destination.droppableId as TeamTaskStatus;
    if (from === to && source.index === destination.index) return;

    const next = { ...columns, [from]: [...columns[from]] };
    if (from !== to) next[to] = [...columns[to]];
    const [moved] = next[from].splice(source.index, 1);
    next[to].splice(destination.index, 0, { ...moved, status: to });
    setColumns(next);

    // Persist only what changed: the moved task's status and the new order of both columns.
    const touched = [...new Set([from, to])].flatMap((status) =>
      next[status]
        .map((task, index) => ({ task, index }))
        .filter(
          ({ task, index }) =>
            task.index !== index || (task.id === moved.id && from !== to),
        ),
    );
    try {
      await Promise.all(
        touched.map(({ task, index }) =>
          dataProvider.update<TeamTask>("team_tasks", {
            id: task.id,
            data: {
              index,
              ...(task.id === moved.id
                ? {
                    status: to,
                    done_at: to === "done" ? new Date().toISOString() : null,
                  }
                : {}),
            },
            // The original record: the PostgREST provider only sends the fields that
            // differ from previousData, and `task` already carries the new status.
            previousData: task.id === moved.id ? moved : task,
          }),
        ),
      );
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
    refresh();
  };

  if (isPending) return <ListSkeleton variant="board" rows={3} />;

  return (
    <KanbanBoard onDragEnd={(result) => void onDragEnd(result)}>
      {TEAM_TASK_STATUSES.map((status) => {
        const tasks = columns[status.id as TeamTaskStatus];
        return (
          <KanbanColumn
            key={status.id}
            title={translate(status.name)}
            count={tasks.length}
            color={status.color}
            action={
              <Link
                to={`/team_tasks/create?source=${encodeURIComponent(
                  JSON.stringify({ status: status.id }),
                )}`}
                className={cn(
                  buttonVariants({ variant: "ghost", size: "icon" }),
                  "-my-1 size-8 rounded-full",
                )}
                aria-label={translate("hub.team_tasks.new")}
              >
                <Plus className="size-4" />
              </Link>
            }
          >
            <KanbanDropZone
              droppableId={status.id}
              isEmpty={tasks.length === 0}
              emptyLabel={translate("hub.team_tasks.empty_column")}
            >
              {tasks.map((task, index) => (
                <TeamTaskCard key={task.id} task={task} index={index} />
              ))}
            </KanbanDropZone>
          </KanbanColumn>
        );
      })}
    </KanbanBoard>
  );
};
